/**
 * Admin Users API client — FE-03/FE-04.
 * Calls GET /admin/users, GET /admin/users/:id,
 * POST /admin/users/:id/freeze, POST /admin/users/:id/unfreeze.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type UserStatus = "active" | "frozen" | "deactivated";

export type AdminUserSummary = {
  readonly id: string;
  readonly email: string;
  readonly custom_id: string | null;
  readonly display_name: string;
  readonly plan_type: string;
  readonly token_balance: number;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly status: UserStatus;
  readonly created_at: string;
};

export type SummaryJobBrief = {
  readonly id: string;
  readonly target_type: string;
  readonly status: string;
  readonly created_at: string;
};

export type AuditRecordBrief = {
  readonly id: string;
  readonly action: string;
  readonly created_at: string;
};

export type BillingEventBrief = {
  readonly id: string;
  readonly entry_type: string;
  readonly amount: number;
  readonly created_at: string;
};

export type AdminUserDetail = {
  readonly id: string;
  readonly email: string;
  readonly custom_id: string | null;
  readonly display_name: string;
  readonly avatar_url: string | null;
  readonly bio: string | null;
  readonly language: string | null;
  readonly theme: string | null;
  readonly last_country: string | null;
  readonly last_locale: string | null;
  readonly plan_type: string;
  readonly plan_status: string;
  readonly subscription_tokens_used: number;
  readonly subscription_tokens_limit: number;
  readonly topup_tokens_remaining: number;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly device_count: number;
  readonly status: UserStatus;
  readonly frozen_at: string | null;
  readonly deactivated_at: string | null;
  readonly created_at: string;
  readonly recent_summary_jobs: ReadonlyArray<SummaryJobBrief>;
  readonly recent_audit_records: ReadonlyArray<AuditRecordBrief>;
  readonly recent_billing_events: ReadonlyArray<BillingEventBrief>;
};

type ListUsersParams = {
  readonly q?: string;
  readonly status?: UserStatus;
  readonly plan?: string;
  readonly limit?: number;
  readonly offset?: number;
};

type ListUsersResponse = {
  readonly items: AdminUserSummary[];
  readonly total: number;
};

// ─── API calls ───

export async function fetchUsers(
  params: ListUsersParams,
): Promise<ListUsersResponse> {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.set("q", params.q);
  if (params.status) searchParams.set("status", params.status);
  if (params.plan) searchParams.set("plan", params.plan);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));

  const qs = searchParams.toString();
  const path = qs ? `/admin/users?${qs}` : "/admin/users";
  const res = await apiFetch<{ data: ListUsersResponse }>(path);
  return res.data;
}

export async function fetchUserDetail(id: string): Promise<AdminUserDetail> {
  const res = await apiFetch<{ data: AdminUserDetail }>(`/admin/users/${id}`);
  return res.data;
}

export async function freezeUser(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { frozen: boolean } }>(`/admin/users/${id}/freeze`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function unfreezeUser(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { unfrozen: boolean } }>(
    `/admin/users/${id}/unfreeze`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}
